import React, { useCallback, useEffect, useState } from "react";
import { C } from "../reporting/reportingStyles";
import { RCard, TablePagination } from "../reporting/ReportingShared";
import { getMembershipConfigAudits } from "../../utils/membershipApi";
import { CONFIG_ACTION_LABELS, FIELD_LABELS } from "./membershipConstants";

/**
 * "Who changed this, and what was it before" — for a tier or for a company.
 *
 * The API has been writing these rows since the concurrency work; this is the screen that
 * makes them readable. An audit trail nobody can open is not an audit trail: the row
 * existing only means the answer is recoverable by somebody with database access, which
 * is not the person being asked "why is my discount 15% again".
 *
 * One component for both entity types, because the record is one table and the two screens
 * differ only by a filter. The member profile's own history stays where it is — different
 * table, different question ("what happened to this person's card").
 *
 * Pages on the SERVER. These rows are append-only and never pruned, so a tier edited
 * weekly for two years has a history no screen should try to render at once.
 */

/**
 * Row bookkeeping, not terms. `revision` in particular would appear in the diff of every
 * single save — it is bumped by definition — and say nothing.
 */
const NOISE_FIELDS = new Set(["id", "merchantId", "revision", "createdAt", "updatedAt", "deletedAt"]);

/**
 * Above this many fields the diff is collapsed.
 *
 * A creation records the whole row, because there is no "before" — around twenty-five
 * fields. Shown open, one creation buries every edit around it, and the edits are what
 * anybody is scrolling for. An ordinary edit touches one to three fields and stays open.
 */
const COLLAPSE_ABOVE = 6;

/**
 * One before → after line. Exported because the member profile renders the same shape.
 *
 * `resolve` turns a stored value into something a person recognises, for the fields where
 * the raw value is an id. The handler change is the one that matters: "Account handler:
 * not set → 7" says nothing, and it is the entry most worth being able to read, since that
 * person can see what everybody in the company spends.
 */
export const DiffRow = ({ field, before, after, resolve }) => {
    const render = (v) => {
        const resolved = resolve?.(field, v);
        if (resolved !== undefined && resolved !== null) return resolved;
        if (v === null || v === undefined || v === "") return <em style={{ color: C.textLight }}>not set</em>;
        if (typeof v === "boolean") return v ? "Yes" : "No";
        // A stored date arrives as an ISO string; showing the raw value is noise.
        if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) return new Date(v).toLocaleDateString();
        // An eligibility or channel list. `[]` reads as "no restriction" everywhere in
        // this feature, and rendering it as an empty string would look like a blank.
        if (Array.isArray(v)) return v.length ? v.join(", ") : <em style={{ color: C.textLight }}>no restriction</em>;
        if (typeof v === "object") return JSON.stringify(v);
        return String(v);
    };
    return (
        <div style={{ fontSize: 12, color: C.textMed }}>
            <strong style={{ color: C.text }}>{FIELD_LABELS[field] || field}:</strong>{" "}
            {render(before)} <span style={{ color: C.textLight }}>→</span> {render(after)}
        </div>
    );
};

/**
 * One entry in a history: what happened, when, by whom, why, and the diff.
 *
 * Shared with the member profile so the two logs cannot drift into describing the same
 * kind of event two different ways. `title` is a node rather than a string because the
 * member log puts the status transition on that line and this one does not.
 */
export const AuditEntry = ({ title, createdAt, adminEmail, reason, previousValues, newValues, resolve }) => {
    const before = previousValues || {};
    const after = newValues || {};
    const fields = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]))
        .filter((f) => !NOISE_FIELDS.has(f));

    const [expanded, setExpanded] = useState(false);
    const collapsible = fields.length > COLLAPSE_ABOVE;
    const shown = collapsible && !expanded ? [] : fields;

    return (
        <div style={{ padding: "12px 0", borderBottom: `1px solid ${C.borderLight}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: C.text }}>{title}</div>
                <div style={{ fontSize: 12, color: C.textLight }}>
                    {createdAt ? new Date(createdAt).toLocaleString() : "—"} · {adminEmail || "system"}
                </div>
            </div>
            {reason && <div style={{ fontSize: 12, color: C.textMed, marginTop: 4 }}>“{reason}”</div>}
            {fields.length > 0 && (
                <div style={{ marginTop: 6, paddingLeft: 12, borderLeft: `2px solid ${C.borderLight}` }}>
                    {shown.map((f) => (
                        <DiffRow key={f} field={f} before={before[f]} after={after[f]} resolve={resolve} />
                    ))}
                    {collapsible && (
                        <button
                            type="button"
                            onClick={() => setExpanded((v) => !v)}
                            style={{
                                marginTop: shown.length ? 6 : 0, padding: 0, border: "none", background: "none",
                                // C.coral is the brand teal — the key kept its pre-rebrand name.
                                color: C.coral, fontSize: 12, fontWeight: 600, cursor: "pointer",
                            }}
                        >
                            {expanded ? "Hide the details" : `Show all ${fields.length} fields`}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

/**
 * `entityType` is mandatory alongside `entityId`: the id is not a foreign key, so tier 5
 * and company 5 both exist and the API refuses an unqualified id rather than returning
 * the two histories mixed together.
 *
 * To refresh it after a change made without leaving the page — appointing a handler, for
 * one — give it a changing `key` and let React remount it. That also returns it to page 1,
 * which is where the new entry is. Both edit forms navigate away after saving, so the
 * mount fetch covers those.
 */
const ConfigAuditLog = ({ entityType, entityId, merchantId, title, resolve }) => {
    const [log, setLog] = useState({ rows: [], total: 0, totalPages: 0 });
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [isLoading, setIsLoading] = useState(true);

    const load = useCallback(async () => {
        if (!entityId || !merchantId) return;
        setIsLoading(true);
        const data = await getMembershipConfigAudits({ merchantId, entityType, entityId, page, limit });
        setLog(data);
        setIsLoading(false);
    }, [entityType, entityId, merchantId, page, limit]);

    useEffect(() => { load(); }, [load]);

    const total = log.total || 0;
    const rows = log.rows || [];

    return (
        <RCard title={title || `📜 Change history · ${total} change${total === 1 ? "" : "s"}`}>
            {isLoading && rows.length === 0 ? (
                <div style={{ textAlign: "center", padding: 30, color: C.textMed }}>Loading…</div>
            ) : rows.length === 0 ? (
                <div style={{ textAlign: "center", padding: 30, color: C.textMed }}>
                    {page > 1 ? "Nothing further back." : "No changes recorded yet."}
                </div>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {rows.map((a) => (
                        <AuditEntry
                            key={a.id}
                            title={CONFIG_ACTION_LABELS[a.action] || a.action}
                            createdAt={a.createdAt}
                            adminEmail={a.adminEmail}
                            reason={a.reason}
                            previousValues={a.previousValues}
                            newValues={a.newValues}
                            resolve={resolve}
                        />
                    ))}
                    <TablePagination
                        total={total}
                        page={page}
                        limit={limit}
                        onPageChange={setPage}
                        onLimitChange={(l) => { setLimit(l); setPage(1); }}
                    />
                </div>
            )}
        </RCard>
    );
};

export default ConfigAuditLog;
